import { useState } from "react"
import { FaPrint } from "react-icons/fa6"
import { showError } from "../../utils/snackbar"
import { useLocalization } from "../../providers/LocalizationProvider"
import { printForm } from "../../utils/formPrintBuilder";
import { selectWebsiteData } from "../../redux/general/selectors";
import { useSelector } from "react-redux";
import { BASE_URL } from "../Alerts/Alerts";

/**
 * Reusable "print this form" button.
 *
 * Drop it into any screen that already has a form schema + values — it doesn't
 * care where they came from (modal, wizard step, read-only detail page, etc).
 * Opens the browser's native print dialog in a new tab (same one you get from
 * Ctrl/Cmd+P) with a formatted, checkbox-annotated snapshot of the form —
 * from there the user can print it or choose "Save as PDF".
 *
 * <FormPdfDownloadButton
 *   formItem={formItem}          // { title_key, slug, schema: { fields } }
 *   formValues={formState}       // current values keyed by field.key
 *   meta={{
 *     logoUrl: "/assets/wehomecare-logo.png", // or logoDataUrl if you already have base64
 *     appName: "WeHomeCare Portal", // optional; falls back to localStorage["app_name"]
 *     orgName: "WeHomeCare",
 *     patientName: patient?.name,
 *     date: new Date().toLocaleDateString("en-GB"),
 *     filledByName: submission?.created_by_name,
 *     filledDate: submission?.created_at,
 *     lastUpdatedDate: submission?.updated_at,
 *     refNumber: formState?.[refFieldKey],
 *   }}
 * />
 *
 * Every key inside `meta` is optional — anything you don't pass is simply left
 * out of the printout, so callers only wire up what they actually have available.
 */
const FormPdfDownloadButton = ({
    formItem,
    formValues,
    meta,
    className = "e-dynamic-detail-form-modal-btn e-dynamic-detail-form-modal-btn-secondary e-dynamic-detail-form-modal-btn-icon p-0",
    iconOnly = true,
    label,
    onStart,
    onComplete,
    onError,
}) => {
    const [isPrinting, setIsPrinting] = useState(false)
    const { voiceiiTags } = useLocalization()
    const websiteData = useSelector(selectWebsiteData)

    const handleClick = () => {
        if (isPrinting) return

        setIsPrinting(true)
        onStart?.()

        try {
            // printForm opens the new tab/window synchronously (inside this click
            // handler) so popup blockers don't intervene, then prints once the
            // content (and logo, if any) is ready.
            printForm({
                formItem,
                formValues,
                meta: {...meta, logoUrl: `${BASE_URL}${websiteData?.logo}`},
                translate: voiceiiTags,
            })
            onComplete?.()
        } catch (error) {
            console.warn("FormPdfDownloadButton error", error)
            showError(voiceiiTags("Failed to open print preview"))
            onError?.(error)
        } finally {
            setIsPrinting(false)
        }
    }

    return (
        <button
            type="button"
            title={voiceiiTags("Print")}
            aria-label={voiceiiTags("Print")}
            disabled={isPrinting}
            className={className}
            onClick={handleClick}
        >
            <FaPrint size={18}/>
            {!iconOnly && <span className="ms-2">{label || voiceiiTags("Print")}</span>}
        </button>
    )
}

export default FormPdfDownloadButton